import * as T from 'three';

const POINTS=22,SAMPLE=1/60,LIFE=.28;
/** Fine condensation ribbons, separate from engine exhaust. Samples live in the
 * planet frame so turning, world carry and floating-origin rebases keep the wake. */
export class CrystalWingAirflow {
  readonly mesh:T.Mesh<T.BufferGeometry,T.ShaderMaterial>;
  strength=0;
  private readonly history=Array.from({length:4},()=>Array.from({length:POINTS},()=>new T.Vector3()));
  private readonly previous=Array.from({length:4},()=>new T.Vector3());
  private readonly sources=Array.from({length:4},()=>new T.Vector3());
  private readonly ages=new Float64Array(POINTS);
  private readonly points=Array.from({length:POINTS},()=>new T.Vector3());
  private readonly tangent=new T.Vector3();
  private readonly probe=new T.Vector3();
  private readonly positions=new T.BufferAttribute(new Float32Array(4*POINTS*2*3),3).setUsage(T.DynamicDrawUsage);
  private readonly tangents=new T.BufferAttribute(new Float32Array(4*POINTS*2*3),3).setUsage(T.DynamicDrawUsage);
  private readonly coords=new T.BufferAttribute(new Float32Array(4*POINTS*2*2),2).setUsage(T.DynamicDrawUsage);
  private count=0;
  private clock=0;
  private accumulator=0;
  constructor(parent:T.Object3D,aura:T.Color){
    const geometry=new T.BufferGeometry(),indices:number[]=[],weights=new Float32Array(4*POINTS*2);
    for(let line=0;line<4;line++){
      for(let i=0;i<POINTS;i++)weights.fill(line%2===0?1:.48,(line*POINTS+i)*2,(line*POINTS+i)*2+2);
      for(let i=0;i<POINTS-1;i++){const a=(line*POINTS+i)*2;indices.push(a,a+1,a+2,a+1,a+3,a+2);}
    }
    geometry.setIndex(indices);geometry.setAttribute('position',this.positions);geometry.setAttribute('tangent',this.tangents);
    geometry.setAttribute('uv',this.coords);geometry.setAttribute('weight',new T.BufferAttribute(weights,1));
    const material=new T.ShaderMaterial({transparent:true,depthWrite:false,side:T.DoubleSide,toneMapped:false,
      uniforms:{strength:{value:0},aura:{value:aura},time:{value:0}},
      vertexShader:`attribute vec3 tangent;attribute float weight;uniform float time;varying vec2 vUv;varying float vWeight,vDepth;
        void main(){vUv=uv;vWeight=weight;vec4 p=modelViewMatrix*vec4(position,1.);
          vec3 t=mat3(modelViewMatrix)*tangent;vec2 side=vec2(-t.y,t.x);
          if(dot(side,side)<.000001)side=vec2(1.,0.);else side=normalize(side);
          float width=.022*weight*(1.-.65*clamp(uv.y,0.,1.));
          p.xy+=side*((uv.x*2.-1.)*width+.025*uv.y*uv.y*sin(uv.y*12.-time*4.));
          vDepth=-p.z;gl_Position=projectionMatrix*p;}`,
      fragmentShader:`uniform float strength,time;uniform vec3 aura;varying vec2 vUv;varying float vWeight,vDepth;
        void main(){float age=vUv.y;float edge=pow(max(0.,1.-abs(vUv.x*2.-1.)),1.5);
          float life=1.-smoothstep(.05,mix(.60,1.,vWeight),age);
          float ripple=.90+.10*sin(age*16.-time*8.);
          // Let the wake pass the close chase camera without filling the lens.
          float a=edge*life*strength*.38*vWeight*ripple*smoothstep(1.8,3.8,vDepth);
          gl_FragColor=vec4(mix(aura,vec3(.90,.95,1.),.94),a);}`,
    });
    this.mesh=new T.Mesh(geometry,material);this.mesh.name='crystal-wing-air-lines';this.mesh.frustumCulled=false;
    this.mesh.renderOrder=4;this.mesh.visible=false;parent.add(this.mesh);
  }
  reset():void{this.count=0;this.accumulator=0;this.strength=0;this.mesh.visible=false;}
  update(dt:number,anchors:readonly T.Vector3[],toFrame:T.Matrix4,toGroup:T.Matrix4,speed:number,enabled:boolean):void{
    if(!enabled||!Number.isFinite(dt)||dt>.2){this.reset();return;}
    if(dt<=0)return;
    for(let i=0;i<4;i++)this.sources[i].copy(anchors[i]).applyMatrix4(toFrame);
    if(this.count&&this.probe.copy(this.history[0][0]).applyMatrix4(toGroup).distanceTo(anchors[0])>Math.max(12,speed*dt*4))this.reset();
    this.clock+=dt;
    const target=T.MathUtils.smoothstep(speed,12,28);
    this.strength+=(target-this.strength)*(1-Math.exp(-(target>this.strength?6:10)*dt));
    if(target===0&&this.strength<.003){this.reset();return;}
    if(!this.count){
      this.count=2;this.ages[0]=this.ages[1]=this.clock;
      for(let line=0;line<4;line++){
        this.previous[line].copy(this.sources[line]);
        this.history[line][0].copy(this.sources[line]);this.history[line][1].copy(this.sources[line]);
      }
    }
    this.accumulator+=dt;
    while(this.accumulator>=SAMPLE-1e-9){
      this.accumulator=Math.max(0,this.accumulator-SAMPLE);
      const end=Math.min(this.count,POINTS-1),blend=T.MathUtils.clamp((dt-this.accumulator)/dt,0,1);
      for(let i=end;i>1;i--){this.ages[i]=this.ages[i-1];for(let line=0;line<4;line++)this.history[line][i].copy(this.history[line][i-1]);}
      this.ages[1]=this.clock-this.accumulator;
      for(let line=0;line<4;line++)this.history[line][1].lerpVectors(this.previous[line],this.sources[line],blend);
      this.count=Math.min(POINTS,this.count+1);
    }
    this.ages[0]=this.clock;
    for(let line=0;line<4;line++){
      this.history[line][0].copy(this.sources[line]);this.previous[line].copy(this.sources[line]);
      for(let i=0;i<POINTS;i++)this.points[i].copy(this.history[line][Math.min(i,this.count-1)]).applyMatrix4(toGroup);
      for(let i=0;i<POINTS;i++){
        const p=this.points[i],age=(this.clock-this.ages[Math.min(i,this.count-1)])/LIFE;
        this.tangent.copy(this.points[Math.max(0,i-1)]).sub(this.points[Math.min(POINTS-1,i+1)]);
        for(let edge=0;edge<2;edge++){
          const index=(line*POINTS+i)*2+edge;
          this.positions.setXYZ(index,p.x,p.y,p.z);this.tangents.setXYZ(index,this.tangent.x,this.tangent.y,this.tangent.z);this.coords.setXY(index,edge,age);
        }
      }
    }
    this.positions.needsUpdate=true;this.tangents.needsUpdate=true;this.coords.needsUpdate=true;
    this.mesh.material.uniforms.strength.value=this.strength;this.mesh.material.uniforms.time.value=this.clock;
    this.mesh.visible=this.strength>.003;
  }
  dispose():void{this.mesh.removeFromParent();this.mesh.geometry.dispose();this.mesh.material.dispose();}
}
